import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TimezoneSelect from './TimezoneSelect';
import { useOptionalAuth } from '../context/AuthContext';

// The Dashboard "Getting started" card. GET /api/dashboard/onboarding decides
// everything: the four ticks, and `show`, which is false once the owner has
// hidden the card or the company has 3+ customers and a route. The Dashboard
// renders this for owners and admins only; the endpoint refuses anyone else.
//
// Nothing renders while loading or when the request fails: a missing
// checklist is harmless, a broken one on the first screen is not.

const STEPS = [
  {
    key: 'pricingDone',
    label: 'Set your pricing tiers',
    button: 'Open pricing',
    to: '/settings?section=pricing',
  },
  {
    key: 'customerDone',
    label: 'Add your first customer',
    button: 'Add customer',
    to: '/customers',
    state: { openAddCustomer: true },
  },
  {
    key: 'signupLinkDone',
    label: 'Share your signup link',
    button: 'Get the link',
    to: '/settings?section=intake',
  },
  {
    key: 'routeDone',
    label: 'Build a route',
    button: 'Go to routes',
    to: '/routes',
  },
];

function GettingStartedCard() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [hiding, setHiding] = useState(false);
  // The company time zone field: the value in the select, a save in flight,
  // and the line under it after a save.
  const [zone, setZone] = useState('');
  const [savingZone, setSavingZone] = useState(false);
  const [zoneMessage, setZoneMessage] = useState('');
  const auth = useOptionalAuth();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/onboarding`)
      .then((res) => {
        if (cancelled) return;
        setState(res.data);
        setZone(res.data.timezone || '');
      })
      .catch((err) => console.error('Failed to load onboarding:', err));
    return () => { cancelled = true; };
  }, []);

  // Same endpoint and rule as Settings > Company: owners and admins only, and
  // the server refuses a zone that is not on the list.
  const handleSaveZone = async () => {
    setSavingZone(true);
    setZoneMessage('');
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/company`, { timezone: zone });
      setState((prev) => ({ ...prev, timezone: zone }));
      setZoneMessage('Time zone saved.');
      if (auth && auth.refreshUser) auth.refreshUser({ silent: true });
    } catch (err) {
      setZoneMessage(err.response?.data?.error || 'Could not save the time zone. Please try again.');
    } finally {
      setSavingZone(false);
    }
  };

  const handleHide = async () => {
    setHiding(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/dashboard/onboarding/dismiss`);
      setState((prev) => ({ ...prev, dismissed: true, show: false }));
    } catch (err) {
      console.error('Failed to hide onboarding:', err);
      setHiding(false);
    }
  };

  if (!state || !state.show) return null;

  const doneCount = STEPS.filter((s) => state[s.key]).length;

  return (
    <section className="card getting-started-card" aria-labelledby="getting-started-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
        <h2 id="getting-started-title" style={{ fontSize: '1.1rem', margin: 0 }}>Getting started</h2>
        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{doneCount} of {STEPS.length} done</span>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {STEPS.map((step) => {
          const done = state[step.key] === true;
          return (
            <li
              key={step.key}
              data-testid={`onboarding-${step.key}`}
              data-done={done ? 'true' : 'false'}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderTop: '1px solid var(--color-border)' }}
            >
              <span
                aria-label={done ? 'Done' : 'Not done'}
                role="img"
                style={{
                  flex: '0 0 22px', height: '22px', borderRadius: '50%', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem',
                  color: done ? '#fff' : 'var(--color-text-light)',
                  background: done ? 'var(--color-success)' : 'transparent',
                  border: done ? 'none' : '2px solid var(--color-border)',
                }}
              >
                {done ? '✓' : ''}
              </span>
              <span style={{ flex: 1, textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--color-text-light)' : 'var(--color-text)' }}>
                {step.label}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(step.to, step.state ? { state: step.state } : undefined)}
              >
                {step.button}
              </button>
            </li>
          );
        })}
      </ol>
      {Array.isArray(state.timezoneOptions) && state.timezoneOptions.length > 0 && (
        <div style={{ padding: '8px 0', borderTop: '1px solid var(--color-border)' }}>
          <label className="form-label" htmlFor="onboardingTimezone">Your time zone</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <TimezoneSelect
              id="onboardingTimezone"
              value={zone}
              options={state.timezoneOptions}
              onChange={(e) => { setZone(e.target.value); setZoneMessage(''); }}
              disabled={savingZone}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleSaveZone}
              disabled={savingZone || !zone || zone === state.timezone}
            >
              {savingZone ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', margin: '4px 0 0' }}>
            Set from your browser when you signed up. Today's date, texting quiet hours and every time shown follow it. You can change it later in Settings &gt; Company.
          </p>
          {zoneMessage && <p role="status" style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>{zoneMessage}</p>}
        </div>
      )}
      <div style={{ textAlign: 'right', marginTop: '8px' }}>
        <button
          type="button"
          onClick={handleHide}
          disabled={hiding}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--color-text-light)', textDecoration: 'underline' }}
        >
          Hide this
        </button>
      </div>
    </section>
  );
}

export default GettingStartedCard;
